// =====================================================================
// src/screens/super-admin/lib/supportChats.js
//
// Admin-side layer for /chats where chatType == 'support'. Complements
// V1's db.js chat helpers (which are participant-scoped and rewrite
// participants on send — safe for peers, unsafe for support chats
// where the participants must remain [ownerUid, aangansupportUid]).
//
// The Firestore rules were extended in Session 5a to grant admin
// (isPlatformAdmin()) read/write access on chats where chatType ==
// 'support'. Peer chats stay entirely private (no chatType field →
// admin branch fails closed).
// =====================================================================

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  onSnapshot,
  setDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';

export const ADMIN_PAGE_SIZE = 25;

// ============================================================
// listSupportChatsForAdmin({ cursor })
// ============================================================
//
// Reads a paginated slice of support chats ordered by lastMessageAt DESC
// (most recently active chat first — matches admin mental model for
// support queue triage).
//
// Args:
//   cursor (DocumentSnapshot | null) — last doc from previous page.
//
// Returns:
//   {
//     chats:   Array<{ id, ...fields }>,
//     lastDoc: DocumentSnapshot | null,
//     hasMore: boolean
//   }
//
// Firestore cost per call: exactly ADMIN_PAGE_SIZE reads (or fewer
// on the last page).
//
// Required composite index: chats (chatType ASC, lastMessageAt DESC).
// Added to firestore.indexes.json in this same session.
//
// Throws: any Firestore error.
//
export async function listSupportChatsForAdmin({ cursor = null } = {}) {
  const constraints = [
    where('chatType', '==', 'support'),
    orderBy('lastMessageAt', 'desc'),
  ];

  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(ADMIN_PAGE_SIZE));

  const q = query(collection(db, 'chats'), ...constraints);
  const snap = await getDocs(q);

  const chats = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  const hasMore = snap.docs.length === ADMIN_PAGE_SIZE;

  return { chats, lastDoc, hasMore };
}

// ============================================================
// sendAdminMessage({ chatId, adminUid, text })
// ============================================================
//
// Sends a message from an admin into a support chat, preserving the
// existing participants array. Critically distinct from V1's
// sendChatMessage helper which rewrites participants on every send
// (safe for peer chats, unsafe here — if admin sent via V1 helper,
// participants would flip to [adminUid, ownerUid], kicking
// aangansupport out of the chat).
//
// Message doc fromId = adminUid (real signed-in admin UID, either
// rajasekhar91.d or aangansupport). The business-side chat UI applies
// the "Aangan Support" display label to any admin UID via
// isAanganSupportSender(); individual authorship is preserved on the
// message doc for internal audit.
//
// Chat-doc update: only touches lastMessage / lastMessageAt / lastReadAt.
// participants stays untouched (Firestore rule requires it unchanged
// on any update).
//
// Args:
//   chatId   (string, required) — the chat doc id (from listSupportChatsForAdmin)
//   adminUid (string, required) — signed-in admin UID (authUser.uid)
//   text     (string, required, non-empty after trim)
//   extra    (object, optional) — Session 5c. Additional structured fields
//            spread onto the message doc (e.g. { type: 'plan-card',
//            planCard: {...} }). Plain text sends pass nothing and the
//            written doc is byte-identical to pre-5c messages.
//
//            Safe against the rules: the support-message create rule has
//            NO keys().hasOnly() allowlist — it only enforces signedIn(),
//            admin-on-a-support-chat, and fromId == request.auth.uid. So
//            extra fields require no rules change.
//
// Firestore cost: 2 writes (message add + chat setDoc merge).
//
// Rules alignment: chat update rule and message create rule were both
// extended to allow admin write on chats where chatType == 'support'.
// fromId == request.auth.uid is still enforced on message create.
//
// Throws: validation errors + any Firestore error.
export async function sendAdminMessage({ chatId, adminUid, text, extra }) {
  if (!chatId) throw new Error('sendAdminMessage: chatId required');
  if (!adminUid) throw new Error('sendAdminMessage: adminUid required');
  const trimmed = (text || '').trim();
  if (!trimmed) throw new Error('sendAdminMessage: text required');

  const chatRef = doc(db, 'chats', chatId);

  // Update chat metadata WITHOUT touching participants. Merge:true means
  // fields we don't include stay unchanged — including participants,
  // which the rule requires to match resource.data.participants
  // exactly.
  await setDoc(
    chatRef,
    {
      lastMessage: trimmed,
      lastMessageAt: serverTimestamp(),
      lastReadAt: { [adminUid]: serverTimestamp() },
    },
    { merge: true },
  );

  // Add the message. Message create rule requires fromId == auth.uid
  // even for admin senders — internal audit signal that shows which
  // real admin actually authored this message.
  //
  // senderRole: 'support' is the display-side discriminator that lets
  // both the business chat modal and this admin inbox render bubbles
  // on the correct side. UID-based checks break in the edge case where
  // Rajasekhar (super admin) also happens to be a business owner —
  // any message from his UID would ambiguously look like both admin
  // and owner. The senderRole tag removes the ambiguity: whichever
  // helper wrote the message declared its intent.
  //
  // Business-side sends via V1's sendChatMessage do NOT add senderRole
  // (V1 is untouched). Display logic treats missing senderRole as
  // 'owner' (safe default — the only path that omits it is the
  // owner-side send).
  const messageData = {
    fromId: adminUid,
    senderRole: 'support',
    text: trimmed,
    sentAt: serverTimestamp(),
    // Session 5c — structured payload (plan cards). Spread LAST but the
    // core fields above are not overridable in practice: callers only
    // pass presentational keys. Undefined/empty extra is a no-op.
    ...(extra && typeof extra === 'object' ? extra : {}),
  };
  const msgRef = await addDoc(collection(db, 'chats', chatId, 'messages'), messageData);
  return { id: msgRef.id, ...messageData };
}

// ============================================================
// PLAN_CARD_TYPE / formatPlanCardText / sendPlanCard — Session 5c
// ============================================================
//
// Admin sends a structured "Aangan Connect plan" card into a support
// chat instead of typing pricing by hand. The merchant sees a rendered
// card with a one-tap "I'd like this plan" CTA.
//
// WIRE CONTRACT (the only thing shared across the admin/V2 trees — the
// merchant renders straight from msg.planCard, no shared import):
//
//   {
//     ...standard admin message fields...
//     type: 'plan-card',
//     planCard: { tierId, amount, durationMonths, label }
//   }
//
// WHY `text` IS STILL SET: two consumers read message.text and know
// nothing about plan cards —
//   1. onChatMessageCreated (Cloud Function) uses it as the FCM push body
//   2. the chat-doc lastMessage preview (inbox rows / chat lists)
// A card without text would push an empty notification. So every card
// carries a human-readable fallback string.
//
// ACKNOWLEDGMENT: message docs are immutable (`allow update: if false`),
// so the merchant's tap cannot flip a flag on the card. It sends a normal
// text REPLY instead (via V1's sendChatMessage). The reply bubble is the
// acknowledgment — admin reads intent and activates via ActivationModal.
//
// COPY DISCIPLINE (banked): the word "subscription" NEVER appears in a
// user-visible string. The noun is "Aangan Connect".
export const PLAN_CARD_TYPE = 'plan-card';

// Human-readable fallback used for the FCM push body and the inbox
// preview. Kept in one place so admin + merchant strings can't drift.
export function formatPlanCardText(tier) {
  return `Aangan Connect plan: ₹${tier.amount} · ${tier.label}`;
}

// sendPlanCard({ chatId, adminUid, tier })
//
//   tier — a Tier from loadSubscriptionConfig(): { id, amount,
//          durationMonths, label }
//
// Firestore cost: 2 writes (same as any admin message).
export async function sendPlanCard({ chatId, adminUid, tier }) {
  if (!tier || !tier.id) throw new Error('sendPlanCard: tier required');
  if (typeof tier.amount !== 'number') throw new Error('sendPlanCard: tier.amount must be a number');
  if (typeof tier.durationMonths !== 'number') throw new Error('sendPlanCard: tier.durationMonths must be a number');

  return sendAdminMessage({
    chatId,
    adminUid,
    text: formatPlanCardText(tier),
    extra: {
      type: PLAN_CARD_TYPE,
      planCard: {
        tierId: tier.id,
        amount: tier.amount,
        durationMonths: tier.durationMonths,
        label: tier.label || `${tier.durationMonths} month${tier.durationMonths === 1 ? '' : 's'}`,
      },
    },
  });
}


// ============================================================
// subscribeToAwaitingSupportChats(callback) — Session 5d.2
// ============================================================
//
// Live list of the IDs of support chats AWAITING AN ADMIN REPLY.
// Returns an unsubscribe function.
//
// Callback receives an Array<chatId>. AdminShell uses .length for the
// sidebar badge and passes the ids down to SupportInboxPage, which dots
// the matching rows. ONE subscription feeds both, so the badge count and
// the marked rows can never disagree — which they would if the page
// derived "awaiting" from its own paginated getDocs snapshot, since that
// list goes stale the moment a merchant writes.
//
// WHY A DENORMALIZED FLAG, NOT A lastReadAt COMPARISON
// ----------------------------------------------------
// The obvious definition of "unread" is lastMessageAt > lastReadAt[adminUid].
// Firestore cannot query a comparison between two fields, so evaluating it
// would mean pulling every support chat into the client on every admin page
// load — exactly the cost AdminShell's header comment refused to pay when it
// deferred sidebar badges. Worse, lastReadAt only advances when someone
// SENDS a message; nothing marks a chat read on open, so "unread" isn't even
// representable from the data we store.
//
// So onChatMessageCreated maintains a boolean `awaitingAdmin` on the chat
// doc: true when a member or merchant writes, false when an admin replies.
// "Awaiting a reply" is also the more useful concept for a support queue —
// a chat you read and didn't answer is still outstanding, so the marker
// clears on REPLY, not on open. Opening a chat you don't answer must not
// make it disappear from the queue.
//
// COST
// ----
// The awaitingAdmin==true filter IS the cost control: the query returns only
// chats that are actually waiting, typically zero to a handful. So this costs
// that many reads on mount plus deltas — not one read per support chat.
// Same query shape as listSupportChatsForAdmin, so the existing /chats read
// rule (isPlatformAdmin() && chatType == 'support') already permits it; no
// rules change was needed.
//
// Equality-only on two fields, no orderBy, so Firestore serves this by index
// merge — no composite index required.
//
// BACKFILL: chats created before this shipped have no awaitingAdmin field,
// and `where('awaitingAdmin','==',true)` does not match a missing field. They
// join the queue on their next message.
export const SUPPORT_UNREAD_CAP = 99;

export function subscribeToAwaitingSupportChats(callback) {
  const q = query(
    collection(db, 'chats'),
    where('chatType', '==', 'support'),
    where('awaitingAdmin', '==', true),
    limit(SUPPORT_UNREAD_CAP),
  );

  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => d.id)),
    (err) => {
      // Fail quiet: a broken badge must never break the admin shell.
      console.error('[supportChats] awaiting-chats subscription error:', err);
      callback([]);
    },
  );
}
